import { ModuleType } from "entities/modules/types";
import React from "react";
import SharedTranslations, {
  TranslationField,
} from "shared/components/Translations";
import TranslationInput from "shared/components/Translations/TranslationInput";
import { LocaleCode } from "shared/types/languages";
import { ManagedTranslations } from "../../../../context/types";
import { useGeneralData } from "../../../../stores/useGeneralData";

const Translations = () => {
  const {
    moduleName,
    setModuleName,
    contentBlocks,
    setContentBlocks,
    moduleType,
    translations,
    setTranslations,
    setTopPrivacyTipsBlock,
    topPrivacyTipsBlock,
  } = useGeneralData();

  const isContentBlocksModule = moduleType === ModuleType.ContentBlocks;
  const isOneCardBlockModule =
    Array.isArray(contentBlocks) &&
    contentBlocks.length > 0 &&
    contentBlocks[0].hasOwnProperty("ctaForRegisteredUser");
  const isTopPrivacyBlockModule = moduleType === ModuleType.TopPrivacyTips;

  const createFields = (): TranslationField[] => {
    let fields: TranslationField[] = [];

    if (
      !isContentBlocksModule ||
      (isContentBlocksModule && !isOneCardBlockModule)
    ) {
      fields.push({
        key: ManagedTranslations.ModuleName,
        label: ManagedTranslations.ModuleName,
        render: ({ language, value, onChange }) => (
          <TranslationInput
            defaultValue={language === LocaleCode.en ? moduleName : value}
            variant="filled"
            onChange={({ target: { value } }) => {
              if (language === LocaleCode.en) {
                setModuleName(value);
              } else {
                onChange(value);
              }
            }}
          />
        ),
      });
    }

    if (isContentBlocksModule && Array.isArray(contentBlocks)) {
      contentBlocks.forEach((block, index) => {
        fields.push({
          key: `${ManagedTranslations.CardTitle}_${index}`,
          label: isOneCardBlockModule
            ? ManagedTranslations.ModuleTitle
            : `${ManagedTranslations.CardTitle} ${index + 1}`,
          render: ({ language, value, onChange }) => (
            <TranslationInput
              defaultValue={language === LocaleCode.en ? block.title : value}
              variant="filled"
              onChange={({ target: { value } }) => {
                if (language === LocaleCode.en) {
                  setContentBlocks(
                    contentBlocks.map((item, i) =>
                      index === i ? { ...item, title: value } : item,
                    ),
                  );
                } else {
                  onChange(value);
                }
              }}
            />
          ),
        });

        fields.push({
          key: `${ManagedTranslations.CardDescription}_${index}`,
          label: isOneCardBlockModule
            ? ManagedTranslations.ModuleDescription
            : `${ManagedTranslations.CardDescription} ${index + 1}`,
          render: ({ language, value, onChange }) => (
            <TranslationInput
              defaultValue={
                language === LocaleCode.en ? block.description : value
              }
              variant="filled"
              onChange={({ target: { value } }) => {
                if (language === LocaleCode.en) {
                  setContentBlocks(
                    contentBlocks.map((item, i) =>
                      index === i ? { ...item, description: value } : item,
                    ),
                  );
                } else {
                  onChange(value);
                }
              }}
            />
          ),
        });

        if (isOneCardBlockModule) {
          fields.push({
            key: `${ManagedTranslations.CTAForRegisteredUser}_${index}`,
            label: ManagedTranslations.CTAForRegisteredUser,
            render: ({ language, value, onChange }) => (
              <TranslationInput
                defaultValue={
                  language === LocaleCode.en
                    ? block.ctaForRegisteredUser
                    : value
                }
                variant="filled"
                onChange={({ target: { value } }) => {
                  if (language === LocaleCode.en) {
                    setContentBlocks(
                      contentBlocks.map((item, i) =>
                        index === i
                          ? { ...item, ctaForRegisteredUser: value }
                          : item,
                      ),
                    );
                  } else {
                    onChange(value);
                  }
                }}
              />
            ),
          });

          fields.push({
            key: `${ManagedTranslations.CTAForUnauthenticatedUser}_${index}`,
            label: ManagedTranslations.CTAForUnauthenticatedUser,
            render: ({ language, value, onChange }) => (
              <TranslationInput
                defaultValue={
                  language === LocaleCode.en
                    ? block.ctaForUnRegisteredUser
                    : value
                }
                variant="filled"
                onChange={({ target: { value } }) => {
                  if (language === LocaleCode.en) {
                    setContentBlocks(
                      contentBlocks.map((item, i) =>
                        index === i
                          ? { ...item, ctaForUnRegisteredUser: value }
                          : item,
                      ),
                    );
                  } else {
                    onChange(value);
                  }
                }}
              />
            ),
          });
        }
      });
    }

    if (isTopPrivacyBlockModule) {
      fields.push(
        {
          key: ManagedTranslations.ModuleTitle,
          label: ManagedTranslations.ModuleTitle,
          render: ({ language, value, onChange }) => (
            <TranslationInput
              defaultValue={
                language === LocaleCode.en ? topPrivacyTipsBlock.title : value
              }
              variant="filled"
              onChange={({ target: { value } }) => {
                if (language === LocaleCode.en) {
                  setTopPrivacyTipsBlock({
                    ...topPrivacyTipsBlock,
                    title: value,
                  });
                } else {
                  onChange(value);
                }
              }}
            />
          ),
        },
        {
          key: ManagedTranslations.ModuleDescription,
          label: ManagedTranslations.ModuleDescription,
          render: ({ language, value, onChange }) => (
            <TranslationInput
              defaultValue={
                language === LocaleCode.en
                  ? topPrivacyTipsBlock.description
                  : value
              }
              variant="filled"
              onChange={({ target: { value } }) => {
                if (language === LocaleCode.en) {
                  setTopPrivacyTipsBlock({
                    ...topPrivacyTipsBlock,
                    description: value,
                  });
                } else {
                  onChange(value);
                }
              }}
            />
          ),
        },
        {
          key: ManagedTranslations.CTAForRegisteredUser,
          label: ManagedTranslations.CTAForRegisteredUser,
          render: ({ language, value, onChange }) => (
            <TranslationInput
              defaultValue={
                language === LocaleCode.en
                  ? topPrivacyTipsBlock.ctaForRegisteredUser
                  : value
              }
              variant="filled"
              onChange={({ target: { value } }) => {
                if (language === LocaleCode.en) {
                  setTopPrivacyTipsBlock({
                    ...topPrivacyTipsBlock,
                    ctaForRegisteredUser: value,
                  });
                } else {
                  onChange(value);
                }
              }}
            />
          ),
        },
        {
          key: ManagedTranslations.CTAForUnauthenticatedUser,
          label: ManagedTranslations.CTAForUnauthenticatedUser,
          render: ({ language, value, onChange }) => (
            <TranslationInput
              defaultValue={
                language === LocaleCode.en
                  ? topPrivacyTipsBlock.ctaForUnRegisteredUser
                  : value
              }
              variant="filled"
              onChange={({ target: { value } }) => {
                if (language === LocaleCode.en) {
                  setTopPrivacyTipsBlock({
                    ...topPrivacyTipsBlock,
                    ctaForUnRegisteredUser: value,
                  });
                } else {
                  onChange(value);
                }
              }}
            />
          ),
        },
      );
    }

    return fields;
  };

  return (
    <SharedTranslations
      fields={createFields()}
      translations={translations}
      setTranslations={setTranslations}
    />
  );
};

export default Translations;
